import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { createDecks } from "./catchphraserDecks";

const CatchphraserCommunity = () => {
  const [language, setLanguage] = useState("no");
  const activeDeck = language === "no" ? "overviewNo" : "overviewEn";

  const [currentSlide, setCurrentSlide] = useState(0);
  const [viewport, setViewport] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 1440,
    height: typeof window !== "undefined" ? window.innerHeight : 900,
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [paceMode, setPaceMode] = useState("guided");
  const [activeOverlay, setActiveOverlay] = useState(null);

  const intervalRef = useRef(null);

  const ui = {
    shell: {
      standard:
        "mx-auto flex min-h-[500px] max-w-7xl flex-col justify-center px-4 py-5 text-center sm:px-6 lg:min-h-[600px] max-h-[720px]:min-h-[430px] max-h-[680px]:min-h-[405px] max-h-[720px]:py-3",
      screenshot:
        "mx-auto flex min-h-[500px] max-w-7xl flex-col justify-center px-4 py-4 text-center sm:px-6 lg:min-h-[600px] max-h-[720px]:min-h-[430px] max-h-[680px]:min-h-[405px]",
      compact:
        "mx-auto flex max-w-7xl flex-col justify-center px-4 py-4 text-center sm:px-6 max-h-[720px]:py-2",
    },

    label: {
      red:
        "mx-auto inline-flex rounded-full border border-red-200 bg-red-50/80 px-6 py-1.5 text-xs font-black uppercase tracking-[0.3em] text-red-700 shadow-sm sm:text-sm max-h-[720px]:px-5 max-h-[720px]:text-[11px]",
      purple:
        "text-xs font-black uppercase tracking-[0.24em] text-purple-700 sm:text-sm max-h-[720px]:text-[10px] max-h-[720px]:tracking-[0.18em]",
      green:
        "text-xs font-black uppercase tracking-[0.24em] text-emerald-700 sm:text-sm max-h-[720px]:text-[10px] max-h-[720px]:tracking-[0.18em]",
      gray:
        "text-xs font-black uppercase tracking-[0.22em] text-gray-400 max-h-[720px]:text-[10px]",
    },

    title: {
      hero:
        "mt-9 font-mono text-[clamp(2.45rem,4.4vw,4.35rem)] font-black leading-[0.95] tracking-tight text-gray-950 max-h-[760px]:mt-7 max-h-[720px]:mt-5 max-h-[720px]:text-[clamp(2.25rem,3.8vw,3.25rem)] max-h-[680px]:text-[2.65rem]",

      main:
        "mx-auto mt-3 max-w-5xl text-[clamp(1.85rem,3.05vw,2.9rem)] font-black leading-[1.04] tracking-tight text-gray-950 max-h-[760px]:text-[clamp(1.7rem,2.65vw,2.45rem)] max-h-[720px]:text-[clamp(1.5rem,2.25vw,2rem)] max-h-[680px]:text-[1.75rem]",

      medium:
        "mx-auto mt-3 max-w-5xl text-[clamp(1.55rem,2.45vw,2.35rem)] font-black leading-[1.06] text-gray-950 max-h-[760px]:text-[clamp(1.4rem,2.15vw,2rem)] max-h-[720px]:mt-2 max-h-[720px]:text-[clamp(1.25rem,1.9vw,1.7rem)] max-h-[680px]:text-[1.45rem]",

      card:
        "text-[clamp(1.15rem,1.55vw,1.45rem)] font-black leading-tight text-gray-950 max-h-[720px]:text-[1.05rem]",

      smallCard:
        "text-[clamp(1.05rem,1.3vw,1.25rem)] font-black leading-tight text-gray-950 max-h-[720px]:text-[0.98rem]",
    },

    text: {
      lead:
        "mx-auto mt-5 max-w-4xl text-[clamp(1rem,1.35vw,1.2rem)] font-semibold leading-relaxed text-gray-600 max-h-[760px]:mt-3 max-h-[720px]:text-sm",
      body:
        "text-[clamp(0.9rem,1.05vw,1.05rem)] font-semibold leading-relaxed text-gray-600 max-h-[720px]:text-sm",
      strong:
        "text-[clamp(0.95rem,1.1vw,1.08rem)] font-black leading-snug text-gray-950 max-h-[720px]:text-[0.82rem]",
      quote:
        "text-[clamp(1.15rem,1.65vw,1.65rem)] italic leading-snug text-gray-700 max-h-[720px]:text-[1.1rem]",
    },

    spacing: {
      titleRule:
        "mx-auto mt-5 h-1.5 w-56 rounded-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-500 sm:w-64 max-h-[720px]:mt-3 max-h-[720px]:h-1",
      section:
        "mt-9 max-h-[760px]:mt-6 max-h-[720px]:mt-4",
      sectionLarge:
        "mt-12 max-h-[760px]:mt-8 max-h-[720px]:mt-6",
      cardGrid:
        "mx-auto grid w-full gap-5 max-h-[720px]:gap-3",
    },

    card: {
      base:
        "rounded-[2rem] border-white/70 bg-white/90 shadow-sm ring-1 ring-white/80",
      pad:
        "px-6 py-6 max-h-[720px]:px-4 max-h-[720px]:py-4",
      icon:
        "mx-auto flex h-16 w-16 items-center justify-center rounded-full text-3xl max-h-[720px]:h-12 max-h-[720px]:w-12 max-h-[720px]:text-2xl",
      softIcon:
        "mx-auto flex h-20 w-20 items-center justify-center rounded-full text-4xl max-h-[720px]:h-14 max-h-[720px]:w-14 max-h-[720px]:text-2xl",
    },

    screenshot: {
      frame:
        "relative overflow-hidden rounded-[2.3rem] border border-white/80 bg-white/92 p-3 shadow-[0_30px_95px_rgba(15,23,42,0.15)] ring-1 ring-gray-200/70 transition-all duration-300 group-hover:scale-[1.006] max-h-[720px]:rounded-[1.9rem] max-h-[720px]:p-2",
      inner:
        "rounded-[1.8rem] border border-gray-100 bg-white px-2 py-2 max-h-[720px]:rounded-[1.4rem]",
      imgLarge:
        "mx-auto max-h-[440px] w-full object-contain contrast-[1.04] brightness-[1.01] max-h-[760px]:max-h-[340px] max-h-[720px]:max-h-[290px] max-h-[680px]:max-h-[255px]",
      imgMedium:
        "mx-auto max-h-[385px] w-full object-contain contrast-[1.04] brightness-[1.01] max-h-[760px]:max-h-[305px] max-h-[720px]:max-h-[265px] max-h-[680px]:max-h-[230px]",
    },

    highlight:
      "mx-auto rounded-[2rem] bg-gradient-to-r from-emerald-100 via-blue-100 to-purple-100 px-8 py-5 shadow-sm ring-1 ring-white/80 max-h-[760px]:py-4 max-h-[720px]:px-6 max-h-[720px]:py-3",
  };

  const paceModes = {
    demo: { label: "Demo", multiplier: 0.65, description: "fast overview" },
    guided: {
      label: "Guided",
      multiplier: 1,
      description: "presentation pace",
    },
    reading: {
      label: "Reading",
      multiplier: 1.8,
      description: "self-paced viewing",
    },
  };

  const asset = (name) =>
    `${process.env.REACT_APP_USER_API_URL}/uploads/screens/${name}`;

  const openOverlay = useCallback((image) => {
    setActiveOverlay(image);
    setIsPlaying(false);
  }, []);

  const closeOverlay = useCallback(() => {
    setActiveOverlay(null);
  }, []);

  useEffect(() => {
    const checkLayout = () => {
      setViewport({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    checkLayout();
    window.addEventListener("resize", checkLayout);
    window.addEventListener("orientationchange", checkLayout);

    return () => {
      window.removeEventListener("resize", checkLayout);
      window.removeEventListener("orientationchange", checkLayout);
    };
  }, []);

  useEffect(() => {
    if (!activeOverlay) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [activeOverlay]);

  const LinkIcon = () => (
    <svg
      className="mr-1.5 h-3 w-3"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
      />
    </svg>
  );

  const CloseIcon = () => (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        d="M6 6L18 18M18 6L6 18"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
    </svg>
  );

  const ExpandIcon = () => (
    <svg
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        d="M8 3H3V8M3 3L9 9M16 3H21V8M21 3L15 9M8 21H3V16M3 21L9 15M16 21H21V16M21 21L15 15"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const ExternalLink = ({ compact = false }) => (
    <a
      href="https://catchphraser.com"
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center rounded-full border border-blue-200 bg-white/85 font-semibold text-blue-700 shadow-sm backdrop-blur-md transition-all hover:-translate-y-0.5 hover:bg-white hover:shadow-md ${
        compact ? "px-2.5 py-1 text-[11px]" : "px-3 py-1.5 text-xs"
      }`}
    >
      <LinkIcon />
      catchphraser.com
    </a>
  );

  const DesktopLink = () => (
    <div className="absolute right-4 top-4 z-20 hidden sm:block max-h-[720px]:right-3 max-h-[720px]:top-3">
      <ExternalLink compact />
    </div>
  );

  const Pill = ({ children, color = "blue" }) => {
    const colors = {
      blue: "bg-blue-50 text-blue-700 ring-blue-200",
      purple: "bg-purple-50 text-purple-700 ring-purple-200",
      green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
      pink: "bg-pink-50 text-pink-700 ring-pink-200",
      gray: "bg-gray-50 text-gray-700 ring-gray-200",
      amber: "bg-amber-50 text-amber-700 ring-amber-200",
      cyan: "bg-cyan-50 text-cyan-700 ring-cyan-200",
    };

    return (
      <span
        className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold ring-1 ${colors[color]}`}
      >
        {children}
      </span>
    );
  };

  const SoftCard = ({ children, className = "" }) => (
    <div
      className={`rounded-3xl border border-white/70 bg-white/85 p-5 shadow-[0_18px_60px_rgba(15,23,42,0.08)] backdrop-blur-sm ${className}`}
    >
      {children}
    </div>
  );

  const EmojiCard = ({ emoji, title, text, className = "" }) => (
    <SoftCard className={`text-center ${className}`}>
      <div className="mb-3 text-3xl">{emoji}</div>
      <h4 className="mb-2 text-lg font-black text-gray-950">{title}</h4>
      {text && <p className="text-sm leading-relaxed text-gray-600">{text}</p>}
    </SoftCard>
  );

  const CleanList = ({ items, accent = "gray" }) => {
    const accents = {
      gray: "border-gray-200/70 text-gray-700",
      blue: "border-blue-100 text-gray-700",
      purple: "border-purple-100 text-gray-700",
      green: "border-emerald-100 text-gray-700",
      amber: "border-amber-100 text-gray-700",
      pink: "border-pink-100 text-gray-700",
    };

    return (
      <div className="space-y-0 text-left">
        {items.map((item) => (
          <div
            key={item}
            className={`border-b py-2.5 text-[15px] font-semibold leading-relaxed last:border-0 ${
              accents[accent] || accents.gray
            }`}
          >
            {item}
          </div>
        ))}
      </div>
    );
  };

  const SectionLabel = ({ children }) => (
    <div className="mx-auto mb-3 inline-flex items-center rounded-full border border-gray-200 bg-white/80 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-gray-500 shadow-sm">
      {children}
    </div>
  );

  const ConceptMark = ({ emoji = "🎤", label = "Speech", sub = "practice" }) => (
    <div className="mx-auto mb-4 flex flex-col items-center">
      <div className="flex h-20 w-20 items-center justify-center rounded-[1.5rem] bg-gradient-to-br from-blue-600 to-purple-600 text-4xl shadow-[0_20px_50px_rgba(15,23,42,0.16)] ring-1 ring-white/50">
        {emoji}
      </div>

      <div className="mt-3 text-xs font-black uppercase tracking-[0.18em] text-gray-500">
        {label}
      </div>

      <div className="text-xs font-semibold text-gray-400">{sub}</div>
    </div>
  );

  const ScreenshotFrame = ({
    src,
    alt,
    caption,
    className = "",
    imgClassName = "",
    zoomable = true,
  }) => {
    const frame = (
      <div
        className={`rounded-[1.75rem] border border-gray-200/80 bg-white p-3 shadow-[0_22px_70px_rgba(15,23,42,0.11)] ${className}`}
      >
        <img
          src={src}
          alt={alt}
          className={`w-full rounded-[1.2rem] border border-gray-100 object-cover ${imgClassName}`}
        />

        {caption && (
          <div className="px-2 pt-2 text-center text-xs font-medium text-gray-500">
            {caption}
          </div>
        )}
      </div>
    );

    if (!zoomable) return frame;

    return (
      <button
        type="button"
        onClick={() => openOverlay({ src, alt, caption })}
        className="group block w-full cursor-zoom-in text-left outline-none focus-visible:ring-4 focus-visible:ring-blue-300/70"
        aria-label={`Open screenshot preview: ${alt || caption || "screenshot"}`}
      >
        <div className="relative transition-transform duration-300 group-hover:scale-[1.008]">
          {frame}

          <div className="pointer-events-none absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full border border-white/70 bg-white/92 px-3 py-1.5 text-xs font-black text-gray-600 opacity-0 shadow-sm backdrop-blur-md transition-opacity duration-300 group-hover:opacity-100">
            <ExpandIcon />
            Open preview
          </div>
        </div>
      </button>
    );
  };

  const ZoomableImage = ({
    src,
    alt,
    caption,
    className = "",
    imgClassName = "",
    children,
  }) => (
    <button
      type="button"
      onClick={() => openOverlay({ src, alt, caption })}
      className={`group block w-full cursor-zoom-in text-left outline-none focus-visible:ring-4 focus-visible:ring-blue-300/70 ${className}`}
      aria-label={`Open screenshot preview: ${alt || caption || "screenshot"}`}
    >
      <div className="relative">
        {children || (
          <img
            src={src}
            alt={alt}
            className={`w-full object-contain ${imgClassName}`}
          />
        )}

        <div className="pointer-events-none absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full border border-white/70 bg-white/92 px-3 py-1.5 text-xs font-black text-gray-600 opacity-0 shadow-sm backdrop-blur-md transition-opacity duration-300 group-hover:opacity-100">
          <ExpandIcon />
          Open preview
        </div>
      </div>
    </button>
  );

  const NumberedList = ({ items, accent = "blue" }) => {
    const color =
      accent === "purple"
        ? "text-purple-600"
        : accent === "green"
        ? "text-emerald-600"
        : accent === "amber"
        ? "text-amber-600"
        : "text-blue-600";

    return (
      <div className="space-y-2.5 text-left">
        {items.map((item, index) => (
          <div key={index} className="flex items-start gap-3">
            <div
              className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-white text-xs font-black shadow-sm ${color}`}
            >
              {index + 1}
            </div>

            <div className="text-[15px] leading-relaxed text-gray-700">
              {item}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const ComparisonTable = ({
    leftTitle,
    rightTitle,
    rows,
    leftColor = "blue",
    rightColor = "purple",
  }) => {
    const leftClasses =
      leftColor === "green"
        ? "from-emerald-50 to-white ring-emerald-200"
        : leftColor === "amber"
        ? "from-amber-50 to-white ring-amber-200"
        : "from-blue-50 to-white ring-blue-200";

    const rightClasses =
      rightColor === "pink"
        ? "from-pink-50 to-white ring-pink-200"
        : rightColor === "green"
        ? "from-emerald-50 to-white ring-emerald-200"
        : "from-purple-50 to-white ring-purple-200";

    return (
      <div className="grid w-full gap-5 md:grid-cols-2">
        <div
          className={`rounded-3xl bg-gradient-to-br p-5 shadow-sm ring-1 ${leftClasses}`}
        >
          <h4 className="mb-4 text-center text-xl font-black text-gray-900">
            {leftTitle}
          </h4>

          <NumberedList items={rows.map((row) => row[0])} accent={leftColor} />
        </div>

        <div
          className={`rounded-3xl bg-gradient-to-br p-5 shadow-sm ring-1 ${rightClasses}`}
        >
          <h4 className="mb-4 text-center text-xl font-black text-gray-900">
            {rightTitle}
          </h4>

          <NumberedList
            items={rows.map((row) => row[1])}
            accent={rightColor === "green" ? "green" : "purple"}
          />
        </div>
      </div>
    );
  };

  const BigStatement = ({ title, children, tone = "blue", compact = false }) => {
    const tones = {
      blue: "from-blue-100 via-white to-purple-100 border-blue-200",
      green: "from-emerald-100 via-white to-blue-100 border-emerald-200",
      purple: "from-purple-100 via-white to-pink-100 border-purple-200",
      amber: "from-amber-100 via-white to-orange-100 border-amber-200",
    };

    return (
      <div
        className={`rounded-3xl border bg-gradient-to-r shadow-[0_18px_50px_rgba(15,23,42,0.08)] ${
          compact ? "p-4" : "p-5"
        } ${tones[tone]}`}
      >
        <div className="mb-1 text-lg font-black text-gray-950">{title}</div>

        {children && (
          <div className="text-sm leading-relaxed text-gray-700 sm:text-base">
            {children}
          </div>
        )}
      </div>
    );
  };

  const Timeline = ({ steps }) => (
    <div className="grid gap-4 md:grid-cols-3">
      {steps.map(([emoji, label, title, text, tone]) => (
        <SoftCard key={label} className={`text-center shadow-none ring-1 ${tone}`}>
          <div className="mb-3 text-4xl">{emoji}</div>

          <div className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-gray-500">
            {label}
          </div>

          <h4 className="mb-2 text-xl font-black text-gray-950">{title}</h4>

          <p className="text-sm leading-relaxed text-gray-600">{text}</p>
        </SoftCard>
      ))}
    </div>
  );

  const Overlay = () => {
    if (!activeOverlay) return null;

    return (
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/82 p-4 backdrop-blur-md sm:p-6"
        role="dialog"
        aria-modal="true"
        aria-label="Expanded screenshot preview"
        onClick={closeOverlay}
      >
        <div
          className="relative flex max-h-[94vh] w-full max-w-[1440px] flex-col"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs font-black uppercase tracking-[0.22em] text-white/45">
                screenshot preview
              </div>

              {(activeOverlay.caption || activeOverlay.alt) && (
                <div className="mt-1 truncate text-sm font-bold text-white/80 sm:text-base">
                  {activeOverlay.caption || activeOverlay.alt}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={closeOverlay}
              className="inline-flex shrink-0 items-center gap-2 rounded-full border border-white/15 bg-white/95 px-4 py-2 text-sm font-black text-gray-900 shadow-lg transition hover:-translate-y-0.5 hover:bg-white hover:shadow-xl focus:outline-none focus-visible:ring-4 focus-visible:ring-white/30"
              aria-label="Close screenshot preview"
            >
              <CloseIcon />
              Close
            </button>
          </div>

          <div className="overflow-hidden rounded-[2rem] border border-white/15 bg-white p-2 shadow-[0_40px_140px_rgba(0,0,0,0.48)] sm:p-3">
            <div className="overflow-hidden rounded-[1.45rem] bg-slate-50 ring-1 ring-black/5">
              <img
                src={activeOverlay.src}
                alt={activeOverlay.alt || ""}
                className="max-h-[82vh] w-full object-contain"
              />
            </div>

            {activeOverlay.caption && (
              <div className="px-4 py-3 text-center text-sm font-semibold text-gray-500">
                {activeOverlay.caption}
              </div>
            )}
          </div>

          <div className="mt-3 text-center text-xs font-semibold text-white/45">
            Press Esc or click outside to close
          </div>
        </div>
      </div>
    );
  };

  const helpers = {
    ui,
    ExternalLink,
    Pill,
    SoftCard,
    EmojiCard,
    CleanList,
    SectionLabel,
    ConceptMark,
    ScreenshotFrame,
    ZoomableImage,
    NumberedList,
    ComparisonTable,
    BigStatement,
    Timeline,
  };

  const decks = useMemo(() => createDecks({ helpers, asset }), []);
  const activeDeckData = decks[activeDeck] || decks.overviewNo || decks.overviewEn;
  const slides = activeDeckData?.slides || [];

  const isNarrow = viewport.width < 760;
  const isTabletPortrait =
    viewport.width < 1024 && viewport.height >= viewport.width;
  const isCompact = isNarrow || isTabletPortrait;

  const isShortLandscape =
    !isCompact && viewport.width >= 1024 && viewport.height < 780;

  const shellPaddingClass = isShortLandscape ? "p-1 sm:p-1.5" : "p-2 sm:p-3";
  const topControlsClass = isShortLandscape ? "mb-1" : "mb-2";
  const progressClass = isShortLandscape ? "mb-1.5 h-1.5" : "mb-3 h-2";
  const slidePaddingClass = isShortLandscape
    ? "p-2 sm:p-3 md:p-4 lg:p-4 xl:p-5"
    : "p-4 sm:p-5 md:p-6 lg:p-7 xl:p-8";
  const footerClass = isShortLandscape ? "mt-1.5" : "mt-3";
  const hintClass = isShortLandscape ? "mt-1" : "mt-2";

  const inferSlideDensity = useCallback(
    (slide, index) => {
      if (slide.density) return slide.density;
      if (activeDeck === "business" && index === slides.length - 1) return "team";
      return "medium";
    },
    [activeDeck, slides.length]
  );

  const getSlideDuration = useCallback(
    (slide, index = currentSlide) => {
      const densityDurations = {
        light: 7000,
        medium: 11000,
        dense: 15000,
        screenshot: 14000,
        team: 18000,
      };

      const density = inferSlideDensity(slide, index);
      const baseDuration =
        slide?.duration || densityDurations[density] || densityDurations.medium;

      return Math.round(baseDuration * paceModes[paceMode].multiplier);
    },
    [currentSlide, inferSlideDensity, paceMode]
  );

  const stopSlideshow = useCallback(() => {
    if (intervalRef.current) clearTimeout(intervalRef.current);
    intervalRef.current = null;
    setIsPlaying(false);
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide((current) => Math.min(current + 1, slides.length - 1));
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((current) => Math.max(current - 1, 0));
  }, []);

  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
  }, []);

  const playSlideshow = useCallback(() => {
    if (activeOverlay) return;
    setIsPlaying((playing) => !playing);
  }, [activeOverlay]);

  useEffect(() => {
    setCurrentSlide(0);
    closeOverlay();
    stopSlideshow();
  }, [language, closeOverlay, stopSlideshow]);

  useEffect(() => {
    if (isCompact) {
      stopSlideshow();
    }
  }, [isCompact, stopSlideshow]);

  useEffect(() => {
    if (!isPlaying || isCompact || activeOverlay || slides.length === 0) {
      if (intervalRef.current) clearTimeout(intervalRef.current);
      intervalRef.current = null;
      return;
    }

    if (intervalRef.current) clearTimeout(intervalRef.current);

    intervalRef.current = setTimeout(() => {
      setCurrentSlide((current) => {
        if (current < slides.length - 1) return current + 1;
        setIsPlaying(false);
        return current;
      });
    }, getSlideDuration(slides[currentSlide], currentSlide));

    return () => {
      if (intervalRef.current) clearTimeout(intervalRef.current);
    };
  }, [
    isPlaying,
    isCompact,
    activeOverlay,
    currentSlide,
    activeDeck,
    paceMode,
    slides,
    getSlideDuration,
  ]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const tag = event.target?.tagName?.toLowerCase();
      const isTyping =
        tag === "input" || tag === "textarea" || event.target?.isContentEditable;

      if (isTyping) return;

      if (event.key === "Escape" && activeOverlay) {
        event.preventDefault();
        closeOverlay();
        return;
      }

      if (activeOverlay) return;
      if (isCompact) return;

      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          prevSlide();
          break;
        case "ArrowRight":
        case " ":
          event.preventDefault();
          nextSlide();
          break;
        case "p":
        case "P":
          event.preventDefault();
          playSlideshow();
          break;
        case "Home":
          event.preventDefault();
          goToSlide(0);
          break;
        case "End":
          event.preventDefault();
          goToSlide(slides.length - 1);
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    activeOverlay,
    closeOverlay,
    goToSlide,
    nextSlide,
    playSlideshow,
    prevSlide,
    slides.length,
    isCompact,
  ]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearTimeout(intervalRef.current);
    };
  }, []);

  const progress = slides.length > 0 ? ((currentSlide + 1) / slides.length) * 100 : 0;

  const activeDurationSeconds =
    slides.length > 0
      ? Math.round(getSlideDuration(slides[currentSlide], currentSlide) / 1000)
      : 0;

  const LanguageSwitch = ({ compact = false }) => (
    <div className="flex rounded-full border border-gray-200 bg-white/80 p-1 shadow-sm backdrop-blur-sm">
      {[
        ["no", "NO"],
        ["en", "EN"],
      ].map(([key, label]) => (
        <button
          key={key}
          type="button"
          onClick={() => setLanguage(key)}
          className={`rounded-full font-black transition-all ${
            compact
              ? "px-3 py-1 text-[11px]"
              : "px-3 py-1.5 text-[11px] sm:text-xs max-h-[720px]:px-2.5 max-h-[720px]:py-1"
          } ${
            language === key
              ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md"
              : "text-gray-500 hover:bg-gray-100 hover:text-gray-800"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );

  if (slides.length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 p-6 text-gray-900">
        <div className="max-w-xl rounded-3xl bg-white p-6 text-center shadow-lg ring-1 ring-gray-200">
          <div className="text-lg font-black">No slides found</div>
          <p className="mt-2 text-sm font-semibold text-gray-600">
            Make sure `catchphraserDecks.jsx` exports `overviewNo` and
            `overviewEn`.
          </p>
        </div>
      </div>
    );
  }

  if (isCompact) {
    return (
      <div className="min-h-screen bg-slate-100 text-gray-900">
        <div className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 px-3 py-3 shadow-sm backdrop-blur-md">
          <div className="flex items-center justify-between gap-2">
            <div className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-bold text-gray-500 shadow-sm">
              {language === "no" ? "Norsk" : "English"} • {slides.length} slides
            </div>

            <div className="flex items-center gap-2">
              <LanguageSwitch compact />
              <ExternalLink compact />
            </div>
          </div>
        </div>

        <main className="mx-auto max-w-3xl space-y-4 px-3 py-4">
          <section className="rounded-[1.75rem] bg-slate-950 p-5 text-white shadow-xl">
            <div className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">
              {language === "no" ? "lesemodus" : "compact reading mode"}
            </div>

            <h1 className="mt-2 text-2xl font-black">
              {language === "no" ? "Norsk versjon" : "English version"}
            </h1>

            <p className="mt-2 text-sm leading-relaxed text-slate-300">
              {language === "no"
                ? "Denne visningen brukes på mobil, nettbrett, smale nettleservinduer og stående skjermer."
                : "This layout is used for phones, tablets, narrow browser windows, and portrait screens."}
            </p>
          </section>

          {slides.map((mobileSlide, index) => (
            <section
              key={`${activeDeck}-${index}`}
              className={`overflow-hidden rounded-[1.75rem] shadow-lg ring-1 ring-black/5 ${
                mobileSlide.bgColor || "bg-white"
              }`}
            >
              <div className="border-b border-black/5 bg-white/75 px-4 py-3 backdrop-blur-sm">
                <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">
                  Slide {index + 1}
                </div>

                {mobileSlide.title && (
                  <h2 className="mt-1 text-lg font-black leading-tight text-slate-950">
                    {mobileSlide.title}
                  </h2>
                )}
              </div>

              <div
                className="
                  overflow-hidden px-3 py-5

                  [&_.absolute]:static
                  [&_.absolute]:mb-3
                  [&_.absolute]:flex
                  [&_.absolute]:justify-center

                  [&_.right-3]:right-auto
                  [&_.top-3]:top-auto
                  [&_.right-4]:right-auto
                  [&_.top-4]:top-auto

                  [&_.max-w-7xl]:max-w-full
                  [&_.max-w-6xl]:max-w-full
                  [&_.max-w-5xl]:max-w-full
                  [&_.max-w-4xl]:max-w-full

                  [&_.space-y-8]:space-y-5
                  [&_.space-y-7]:space-y-5
                  [&_.space-y-6]:space-y-4
                  [&_.space-y-5]:space-y-4

                  [&_.grid]:grid-cols-1
                  [&_.grid]:gap-4

                  [&_h1]:text-3xl
                  [&_h1]:leading-tight
                  [&_h2]:text-2xl
                  [&_h3]:text-2xl
                  [&_h4]:text-lg

                  [&_p]:text-sm
                  [&_p]:leading-relaxed

                  [&_.text-xl]:text-base
                  [&_.text-lg]:text-sm
                  [&_.text-4xl]:text-3xl
                  [&_.text-5xl]:text-3xl
                  [&_.text-6xl]:text-4xl
                  [&_.text-7xl]:text-4xl

                  [&_.p-5]:p-4
                  [&_.px-4]:px-2

                  [&_img]:max-h-none
                  [&_img]:object-contain
                "
              >
                {mobileSlide.content}
              </div>
            </section>
          ))}
        </main>

        <Overlay />
      </div>
    );
  }

  return (
    <div
      className={`h-screen overflow-hidden bg-[radial-gradient(circle_at_top_left,_#e0f2fe,_transparent_28%),radial-gradient(circle_at_bottom_right,_#f3e8ff,_transparent_30%),linear-gradient(135deg,#f8fafc,#f1f5f9)] text-gray-900 ${shellPaddingClass}`}
    >
      <div className="mx-auto flex h-full w-full max-w-[1480px] flex-col">
        <div
          className={`${topControlsClass} flex shrink-0 flex-col items-start justify-between gap-2 lg:flex-row lg:items-center max-h-[720px]:gap-1`}
        >
          <div className="inline-flex items-center rounded-full border border-gray-200 bg-white/80 px-3 py-1 text-xs font-bold text-gray-500 shadow-sm backdrop-blur-sm sm:text-sm max-h-[720px]:text-[11px]">
            {language === "no" ? "Norsk" : "English"} • {currentSlide + 1} /{" "}
            {slides.length}

            {isPlaying && (
              <span className="ml-2 text-blue-600">
                • {activeDurationSeconds}s
              </span>
            )}
          </div>

          <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center lg:w-auto max-h-[720px]:gap-1">
            <LanguageSwitch />

            <div className="flex rounded-full border border-gray-200 bg-white/80 p-1 shadow-sm backdrop-blur-sm">
              {Object.entries(paceModes).map(([key, mode]) => (
                <button
                  key={key}
                  onClick={() => setPaceMode(key)}
                  className={`rounded-full px-3 py-1.5 text-[11px] font-black transition-all sm:text-xs max-h-[720px]:px-2.5 max-h-[720px]:py-1 ${
                    paceMode === key
                      ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md"
                      : "text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                  }`}
                  title={mode.description}
                >
                  {mode.label}
                </button>
              ))}
            </div>

            <button
              onClick={playSlideshow}
              className={`flex flex-1 items-center justify-center rounded-full px-4 py-2 text-xs font-black text-white shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-xl sm:flex-none sm:text-sm max-h-[720px]:px-3 max-h-[720px]:py-1.5 max-h-[720px]:text-[11px] ${
                isPlaying
                  ? "bg-gradient-to-r from-red-500 to-orange-500"
                  : "bg-gradient-to-r from-blue-600 to-purple-600"
              }`}
            >
              {isPlaying ? "Pause" : "Play"}
            </button>
          </div>
        </div>

        <div
          className={`${progressClass} shrink-0 overflow-hidden rounded-full bg-white/70 shadow-inner ring-1 ring-gray-200`}
        >
          <div
            className="h-full rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="min-h-0 flex-1">
          <div
            className={`relative mx-auto h-full w-full overflow-hidden rounded-[1.75rem] shadow-[0_30px_100px_rgba(15,23,42,0.14)] ring-1 ring-black/5 transition-all duration-300 sm:rounded-[2rem] ${
              slides[currentSlide].bgColor || "bg-white"
            }`}
          >
            {currentSlide > 0 && <DesktopLink />}

            <div className={`flex h-full flex-col ${slidePaddingClass}`}>
              {slides[currentSlide].title && (
                <div className="mb-3 shrink-0 text-center max-h-[720px]:mb-1.5">
                  <h2 className="font-mono text-base font-black tracking-tight text-gray-950 sm:text-lg md:text-xl lg:text-2xl max-h-[720px]:text-base">
                    {slides[currentSlide].title}
                  </h2>

                  <div className="mx-auto mt-1.5 h-1 w-20 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 sm:w-28 max-h-[720px]:mt-1 max-h-[720px]:h-0.5" />
                </div>
              )}

              <div className="min-h-0 flex-1 overflow-hidden">
                <div className="flex h-full w-full items-center justify-center">
                  <div
                    className="
                      flex h-full w-full origin-center items-center justify-center

                      [&_.min-h-\[620px\]]:min-h-[520px]
                      [&_.min-h-\[640px\]]:min-h-[540px]
                      max-h-[760px]:[&_.min-h-\[620px\]]:!min-h-[480px]
                      max-h-[760px]:[&_.min-h-\[640px\]]:!min-h-[500px]
                      max-h-[720px]:[&_.min-h-\[620px\]]:!min-h-[445px]
                      max-h-[720px]:[&_.min-h-\[640px\]]:!min-h-[465px]
                      max-h-[680px]:[&_.min-h-\[620px\]]:!min-h-[410px]
                      max-h-[680px]:[&_.min-h-\[640px\]]:!min-h-[430px]

                      max-h-[760px]:[&_h1]:!text-[4.2rem]
                      max-h-[720px]:[&_h1]:!text-[3.5rem]
                      max-h-[680px]:[&_h1]:!text-[3rem]

                      max-h-[760px]:[&_h2]:!text-[3rem]
                      max-h-[720px]:[&_h2]:!text-[2.35rem]
                      max-h-[680px]:[&_h2]:!text-[2rem]

                      max-h-[760px]:[&_h3]:!text-[2.45rem]
                      max-h-[720px]:[&_h3]:!text-[2rem]
                      max-h-[680px]:[&_h3]:!text-[1.7rem]

                      max-h-[760px]:[&_h4]:!text-[1.6rem]
                      max-h-[720px]:[&_h4]:!text-[1.35rem]
                      max-h-[680px]:[&_h4]:!text-[1.15rem]

                      max-h-[760px]:[&_h1]:!leading-[0.95]
                      max-h-[760px]:[&_h2]:!leading-[1.02]
                      max-h-[760px]:[&_h3]:!leading-[1.05]

                      [&_.slide-title-xl]:text-5xl
                      sm:[&_.slide-title-xl]:text-6xl
                      md:[&_.slide-title-xl]:text-7xl
                      xl:[&_.slide-title-xl]:text-[6rem]
                      max-h-[760px]:[&_.slide-title-xl]:!text-[4.2rem]
                      max-h-[720px]:[&_.slide-title-xl]:!text-[3.5rem]
                      max-h-[680px]:[&_.slide-title-xl]:!text-[3rem]

                      [&_.slide-title-lg]:text-4xl
                      sm:[&_.slide-title-lg]:text-5xl
                      xl:[&_.slide-title-lg]:text-[4rem]
                      max-h-[760px]:[&_.slide-title-lg]:!text-[3rem]
                      max-h-[720px]:[&_.slide-title-lg]:!text-[2.35rem]
                      max-h-[680px]:[&_.slide-title-lg]:!text-[2rem]

                      [&_.slide-title-md]:text-3xl
                      sm:[&_.slide-title-md]:text-4xl
                      xl:[&_.slide-title-md]:text-[3.35rem]
                      max-h-[760px]:[&_.slide-title-md]:!text-[2.45rem]
                      max-h-[720px]:[&_.slide-title-md]:!text-[2rem]
                      max-h-[680px]:[&_.slide-title-md]:!text-[1.7rem]

                      [&_.mt-16]:mt-12
                      [&_.mt-12]:mt-9
                      [&_.mt-11]:mt-8
                      [&_.mt-10]:mt-7
                      [&_.mt-9]:mt-6
                      [&_.mt-8]:mt-5
                      [&_.mt-7]:mt-5
                      [&_.mt-6]:mt-4

                      max-h-[760px]:[&_.mt-16]:!mt-8
                      max-h-[760px]:[&_.mt-12]:!mt-6
                      max-h-[760px]:[&_.mt-11]:!mt-6
                      max-h-[760px]:[&_.mt-10]:!mt-5
                      max-h-[760px]:[&_.mt-9]:!mt-5
                      max-h-[760px]:[&_.mt-8]:!mt-4
                      max-h-[760px]:[&_.mt-7]:!mt-4
                      max-h-[760px]:[&_.mt-6]:!mt-3

                      max-h-[720px]:[&_.mt-16]:!mt-6
                      max-h-[720px]:[&_.mt-12]:!mt-4
                      max-h-[720px]:[&_.mt-11]:!mt-4
                      max-h-[720px]:[&_.mt-10]:!mt-4
                      max-h-[720px]:[&_.mt-9]:!mt-3
                      max-h-[720px]:[&_.mt-8]:!mt-3
                      max-h-[720px]:[&_.mt-7]:!mt-3
                      max-h-[720px]:[&_.mt-6]:!mt-2

                      max-h-[760px]:[&_.p-8]:!p-6
                      max-h-[760px]:[&_.p-7]:!p-5
                      max-h-[760px]:[&_.p-6]:!p-5
                      max-h-[760px]:[&_.p-5]:!p-4
                      max-h-[760px]:[&_.py-10]:!py-7
                      max-h-[760px]:[&_.py-8]:!py-5
                      max-h-[760px]:[&_.py-6]:!py-4
                      max-h-[760px]:[&_.px-8]:!px-6

                      max-h-[720px]:[&_.p-8]:!p-5
                      max-h-[720px]:[&_.p-7]:!p-4
                      max-h-[720px]:[&_.p-6]:!p-4
                      max-h-[720px]:[&_.p-5]:!p-3
                      max-h-[720px]:[&_.py-10]:!py-5
                      max-h-[720px]:[&_.py-8]:!py-4
                      max-h-[720px]:[&_.py-6]:!py-3
                      max-h-[720px]:[&_.px-8]:!px-5

                      max-h-[760px]:[&_.min-h-\[305px\]]:!min-h-[255px]
                      max-h-[720px]:[&_.min-h-\[305px\]]:!min-h-[230px]
                      max-h-[680px]:[&_.min-h-\[305px\]]:!min-h-[210px]

                      max-h-[760px]:[&_.min-h-\[390px\]]:!min-h-[320px]
                      max-h-[720px]:[&_.min-h-\[390px\]]:!min-h-[285px]
                      max-h-[680px]:[&_.min-h-\[390px\]]:!min-h-[255px]

                      max-h-[760px]:[&_.min-h-\[320px\]]:!min-h-[270px]
                      max-h-[720px]:[&_.min-h-\[320px\]]:!min-h-[235px]
                      max-h-[680px]:[&_.min-h-\[320px\]]:!min-h-[210px]

                      max-h-[760px]:[&_.h-24]:!h-[4.5rem]
                      max-h-[760px]:[&_.w-24]:!w-[4.5rem]
                      max-h-[720px]:[&_.h-24]:!h-14
                      max-h-[720px]:[&_.w-24]:!w-14
                      max-h-[760px]:[&_.h-20]:!h-16
                      max-h-[760px]:[&_.w-20]:!w-16
                      max-h-[720px]:[&_.h-20]:!h-14
                      max-h-[720px]:[&_.w-20]:!w-14
                      max-h-[760px]:[&_.h-16]:!h-12
                      max-h-[760px]:[&_.w-16]:!w-12

                      max-h-[760px]:[&_.text-\[1\.55rem\]]:!text-[1.28rem]
                      max-h-[720px]:[&_.text-\[1\.55rem\]]:!text-[1.08rem]
                      max-h-[760px]:[&_.text-\[1\.45rem\]]:!text-[1.2rem]
                      max-h-[720px]:[&_.text-\[1\.45rem\]]:!text-[1.05rem]
                      max-h-[760px]:[&_.text-\[1\.08rem\]]:!text-[0.95rem]
                      max-h-[720px]:[&_.text-\[1\.08rem\]]:!text-[0.82rem]

                      max-h-[760px]:[&_p]:!leading-snug
                      max-h-[720px]:[&_p]:!leading-snug

                      max-h-[760px]:[&_img]:max-h-[410px]
                      max-h-[720px]:[&_img]:max-h-[350px]
                      max-h-[680px]:[&_img]:max-h-[310px]
                    "
                  >
                    {slides[currentSlide].content}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className={`${footerClass} flex shrink-0 items-center justify-between gap-2 max-h-[720px]:mt-1`}
        >
          <button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className={`rounded-full px-4 py-2 text-xs font-bold transition sm:text-sm max-h-[720px]:px-3 max-h-[720px]:py-1.5 max-h-[720px]:text-[11px] ${
              currentSlide === 0
                ? "cursor-not-allowed text-gray-400"
                : "bg-white/80 text-gray-700 shadow-sm hover:-translate-y-0.5 hover:bg-white hover:shadow-md"
            }`}
          >
            ← {language === "no" ? "Forrige" : "Previous"}
          </button>

          <div className="flex max-w-[55%] flex-wrap justify-center gap-1.5 max-h-[720px]:gap-1">
            {slides.map((_, index) => (
              <button
                key={`${activeDeck}-${index}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
                className={`h-2 rounded-full transition-all max-h-[720px]:h-1.5 ${
                  currentSlide === index
                    ? isPlaying
                      ? "w-6 animate-pulse bg-emerald-500 max-h-[720px]:w-5"
                      : "w-6 bg-blue-600 max-h-[720px]:w-5"
                    : "w-2 bg-gray-300 hover:bg-gray-400 max-h-[720px]:w-1.5"
                }`}
              />
            ))}
          </div>

          <button
            onClick={nextSlide}
            disabled={currentSlide === slides.length - 1}
            className={`rounded-full px-4 py-2 text-xs font-bold transition sm:text-sm max-h-[720px]:px-3 max-h-[720px]:py-1.5 max-h-[720px]:text-[11px] ${
              currentSlide === slides.length - 1
                ? "cursor-not-allowed text-gray-400"
                : "bg-white/80 text-gray-700 shadow-sm hover:-translate-y-0.5 hover:bg-white hover:shadow-md"
            }`}
          >
            {currentSlide === slides.length - 1
              ? language === "no"
                ? "Ferdig"
                : "Finish"
              : language === "no"
              ? "Neste →"
              : "Next →"}
          </button>
        </div>

        <div
          className={`${hintClass} shrink-0 text-center text-xs font-medium text-gray-400 max-h-[720px]:hidden`}
        >
          {language === "no"
            ? "← → for å navigere • Mellomrom for neste • P for start/pause"
            : "← → to navigate • Space to advance • P to play/pause"}
        </div>
      </div>

      <Overlay />
    </div>
  );
};

export default CatchphraserCommunity;